import clsx from 'clsx';
import type { PropsWithChildren } from 'react';

import { Flex } from '../../../../../layouts/flex/Flex.js';
import { interactionsOverlay } from '../../../../core/components/interactions-overlay/InteractionsOverlay.sty.js';
import { useOverlay } from '../../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import type { Data } from '../../../../core/components/overlay-tooltip/types.js';
import { useSeriesActions } from '../../../../core/components/series-action/ChartSeriesActionContext.js';
import { DEFAULT_VALUE_ACCESSOR } from '../../../../core/constants.js';
import { MeterBarChartTooltip } from '../../../shared/components/meter-bar-tooltip/MeterBarChartTooltip.js';
import { useFormatter } from '../../../shared/context/FormatterContext.js';
import { useMeterBarLayoutSize } from '../../../shared/context/MeterBarLayoutSizeContext.js';
import { elementSize } from '../../../shared/styles/element-size.sty.js';
import { container } from '../../../shared/styles.sty.js';
import { getNumericValue } from '../../../shared/utils/get-numeric-value.js';
import { useMeterBarChartData } from '../../context/MeterBarChartDataContext.js';
import { useMeterBarChartInternalConfig } from '../../context/MeterBarChartInternalConfigContext.js';
import { useMeterBarChartTooltipHandlers } from '../../hooks/useMeterBarChartTooltipHandlers.js';

export const MeterBarChartOverlayWithTooltip = (props: PropsWithChildren) => {
  const { size } = useMeterBarLayoutSize();
  const { color, name, value, valueAccessor } = useMeterBarChartData();
  const { tooltip: tooltipConfig } = useMeterBarChartInternalConfig();
  const { formatter, unit } = useFormatter();
  const { chartSeriesActions } = useSeriesActions();
  const overlay = useOverlay();

  const displayValue = getNumericValue(
    value,
    valueAccessor ?? DEFAULT_VALUE_ACCESSOR,
  );
  const item = {
    name: tooltipConfig?.name ?? name ?? '',
    value: formatter(displayValue, unit),
  };
  const data: Data = {
    geometry: 'meterbar' as const,
    series: [
      {
        name: item.name,
        value: item.value,
        color,
      },
    ],
  };
  const { onPointerLeave, onClick, onPointerMove } =
    useMeterBarChartTooltipHandlers(data);

  return (
    <Flex
      ref={overlay.overlayRef}
      className={clsx(container, elementSize({ size }), interactionsOverlay)}
      data-testid="overlay-wrapper"
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
    >
      <MeterBarChartTooltip
        actionsForItem={
          chartSeriesActions
            ? () => () =>
                chartSeriesActions({
                  id: `${name}-${displayValue}`,
                  name: item.name,
                  values: [item.value],
                })
            : undefined
        }
      />
      {props.children}
    </Flex>
  );
};

MeterBarChartOverlayWithTooltip['displayName'] =
  'MeterBarChartOverlayWithTooltip';
